import { Component, input, output, signal, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ComponentSize } from '../types';

export interface DropdownMenuItem {
  label: string;
  icon?: string;
  route?: string;
  action?: () => void;
  disabled?: boolean;
  divider?: boolean;
  children?: DropdownMenuItem[];
}

@Component({
  selector: 'app-dropdown-menu',
  imports: [CommonModule, RouterModule],
  template: `
    <div class="relative inline-block" #container>
      <ng-content select="[trigger]"></ng-content>
      
      @if (isOpen()) {
        <div
          #menu
          [class]="menuClasses"
          [style]="menuStyles()"
          role="menu"
          (click)="$event.stopPropagation()">
          @for (item of items(); track item.label) {
            @if (item.divider) {
              <div class="my-1 border-t border-gray-200 dark:border-gray-700"></div>
            } @else if (item.children && item.children.length > 0) {
              <div class="relative group">
                <button
                  type="button"
                  [disabled]="item.disabled"
                  [class]="getItemClasses(item)"
                  (click)="toggleSubmenu(item)"
                  [attr.aria-expanded]="isSubmenuOpen(item)">
                  @if (item.icon) {
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                    </svg>
                  }
                  <span class="flex-1 text-left">{{ item.label }}</span>
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                @if (isSubmenuOpen(item)) {
                  <div [class]="submenuClasses">
                    @for (child of item.children; track child.label) {
                      @if (child.route) {
                        <a
                          [routerLink]="child.route"
                          [class]="getItemClasses(child)"
                          (click)="closeMenu()">
                          @if (child.icon) {
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="child.icon" />
                            </svg>
                          }
                          <span>{{ child.label }}</span>
                        </a>
                      } @else {
                        <button
                          type="button"
                          [disabled]="child.disabled"
                          [class]="getItemClasses(child)"
                          (click)="handleAction(child)">
                          @if (child.icon) {
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="child.icon" />
                            </svg>
                          }
                          <span>{{ child.label }}</span>
                        </button>
                      }
                    }
                  </div>
                }
              </div>
            } @else if (item.route) {
              <a
                [routerLink]="item.route"
                [class]="getItemClasses(item)"
                (click)="closeMenu()">
                @if (item.icon) {
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                  </svg>
                }
                <span>{{ item.label }}</span>
              </a>
            } @else {
              <button
                type="button"
                [disabled]="item.disabled"
                [class]="getItemClasses(item)"
                (click)="handleAction(item)">
                @if (item.icon) {
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                  </svg>
                }
                <span>{{ item.label }}</span>
              </button>
            }
          }
        </div>
      }
    </div>
  `,
  styles: []
})
export class DropdownMenuComponent {
  items = input<DropdownMenuItem[]>([]);
  placement = input<'bottom' | 'top' | 'left' | 'right'>('bottom');
  size = input<ComponentSize>('md');

  itemClickEvent = output<DropdownMenuItem>();

  @ViewChild('container', { static: false }) container!: ElementRef<HTMLElement>;
  @ViewChild('menu', { static: false }) menu!: ElementRef<HTMLElement>;

  isOpen = signal(false);
  openSubmenus = signal<Set<string>>(new Set());
  menuStyles = signal<{ [key: string]: string }>({});

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.isOpen()) return;
    const target = event.target as HTMLElement;
    if (this.container?.nativeElement?.contains(target)) return;
    this.closeMenu();
  }

  toggleSubmenu(item: DropdownMenuItem): void {
    const current = new Set(this.openSubmenus());
    if (current.has(item.label)) {
      current.delete(item.label);
    } else {
      current.add(item.label);
    }
    this.openSubmenus.set(current);
  }

  isSubmenuOpen(item: DropdownMenuItem): boolean {
    return this.openSubmenus().has(item.label);
  }

  handleAction(item: DropdownMenuItem): void {
    if (item.disabled) return;
    if (item.action) {
      item.action();
    }
    this.itemClickEvent.emit(item);
    this.closeMenu();
  }

  closeMenu(): void {
    this.isOpen.set(false);
    this.openSubmenus.set(new Set());
  }

  getItemClasses(item: DropdownMenuItem): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-5 py-3 text-lg',
      xl: 'px-6 py-4 text-xl'
    };

    const classes = [
      'w-full',
      'flex',
      'items-center',
      'gap-2',
      'rounded-md',
      'transition-colors',
      'duration-150',
      'text-left',
      sizeMap[this.size()]
    ];

    if (item.disabled) {
      classes.push('opacity-50', 'cursor-not-allowed');
    } else {
      classes.push('hover:bg-gray-100', 'dark:hover:bg-gray-700', 'cursor-pointer');
    }

    classes.push('text-gray-700', 'dark:text-gray-300');

    return classes.join(' ');
  }

  get menuClasses(): string {
    return 'absolute z-50 min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 mt-1';
  }

  get submenuClasses(): string {
    return 'absolute left-full top-0 ml-1 min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1';
  }
}

